'use client';

import { useEffect, useRef, useState } from 'react';
import { type LucideIcon } from 'lucide-react';

/*
 * Shared pieces for the three relationship-map illustrations, drawn in the banner graphics' language
 * (components/ProspectingGraphic.tsx: round record nodes with icons, beams that draw in, contact rings, status dots)
 * but in the light theme: white nodes, blue-tinted gray rims and links, blue for motion and emphasis.
 * Everything animates with CSS, held paused until the map scrolls into view, so it plays once per visit.
 */

export const FILL = '#ffffff', RIM = '#cbd5e1', EDGE = '#e2e8f0', ICON = '#64748b', TEXT = '#0f172a', MUTED = '#64748b';
export const BLUE = '#3b82f6', BLUE_SOFT = '#dbeafe';
export type Status = 'ok' | 'debt' | 'risk';
export const STATUS: Record<Status, string> = { ok: '#10b981', debt: '#f59e0b', risk: '#ef4444' };
export const STATUS_TEXT: Record<Status, string> = { ok: '#047857', debt: '#b45309', risk: '#b91c1c' };
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

export const MAP_CSS = `
  @keyframes rm-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
  @keyframes rm-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes rm-reveal { from { opacity: .35; } to { opacity: 1; } }
  @keyframes rm-pop { from { opacity: 0; transform: scale(.6); } to { opacity: 1; transform: scale(1); } }
  .rm-map:not(.rm-go) .rm-a { animation-play-state: paused; }
  .rm-pop { transform-box: fill-box; transform-origin: center; }
  .rm-name { font-family: inherit; font-size: 13px; font-weight: 600; }
  .rm-sub { font-family: inherit; font-size: 11.5px; font-weight: 500; }
  .rm-cap { font-family: inherit; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
  @media (prefers-reduced-motion: reduce) { .rm-a { animation: none !important; } }
`;

// adds `rm-go` to the map once it is on screen, which starts its animations
export function useMapGo<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [go, setGo] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setGo(true); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, `rm-map${go ? ' rm-go' : ''}`] as const;
}

export const fadeIn = (delay: number, dur = 0.4) => ({ animation: `rm-in ${dur}s ${EASE} ${delay}s both` });
export const popIn = (delay: number, dur = 0.35) => ({ animation: `rm-pop ${dur}s ${EASE} ${delay}s both` });

export type Pt = { x: number; y: number; r: number };

// a link's end points sit just outside each node's rim
export function rimLine(A: Pt, B: Pt) {
  const dx = B.x - A.x, dy = B.y - A.y, len = Math.hypot(dx, dy) || 1, ux = dx / len, uy = dy / len;
  const f = (v: number) => v.toFixed(1);
  return `M${f(A.x + ux * (A.r + 3))} ${f(A.y + uy * (A.r + 3))}L${f(B.x - ux * (B.r + 3))} ${f(B.y - uy * (B.r + 3))}`;
}

// the quiet link that is always there
export const Link = ({ d, dashed }: { d: string; dashed?: boolean }) => (
  <path d={d} stroke={dashed ? RIM : EDGE} strokeWidth={dashed ? 1.25 : 1.5} strokeDasharray={dashed ? '4 4' : undefined} />
);

// a blue beam that draws itself along a link: a soft wide halo under a crisp line (no blur filter, so it stays smooth)
// (`glow={false}` drops the halo and leaves just the line)
export function Beam({ d, delay, dur = 0.6, color = BLUE, glow = true }: { d: string; delay: number; dur?: number; color?: string; glow?: boolean }) {
  const style = { animation: `rm-draw ${dur}s ${EASE} ${delay}s both` };
  return (
    <>
      {glow && <path d={d} pathLength={1} strokeDasharray={1} className="rm-a" style={style} stroke={color} strokeOpacity=".18" strokeWidth="6" strokeLinecap="round" />}
      <path d={d} pathLength={1} strokeDasharray={1} className="rm-a" style={style} stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </>
  );
}

// a record: white circle, gray rim, icon in the middle
export function RecordNode({ n, icon: Icon, tint, iconColor = ICON }: { n: Pt; icon: LucideIcon; tint?: string; iconColor?: string }) {
  const s = Math.round(n.r * 0.95);
  return (
    <g>
      <circle cx={n.x} cy={n.y} r={n.r} fill={tint ?? FILL} stroke={RIM} strokeWidth="1.25" />
      <Icon x={n.x - s / 2} y={n.y - s / 2} width={s} height={s} strokeWidth={1.75} color={iconColor} />
    </g>
  );
}

// the highlight ring a node gets when a beam reaches it, drawn around from the top, right over the node's own rim, so
// the node only ever has one outline
export function ContactRing({ n, delay, color = BLUE, gap = 0 }: { n: Pt; delay: number; color?: string; gap?: number }) {
  const r = n.r + gap;
  return (
    <circle cx={n.x} cy={n.y} r={r} pathLength={1} strokeDasharray={1} transform={`rotate(-90 ${n.x} ${n.y})`}
      className="rm-a" style={{ animation: `rm-draw .4s ${EASE} ${delay}s both` }} stroke={color} strokeWidth="1.75" />
  );
}

// a status dot on a node's upper-right rim, with a soft halo
export function StatusDot({ n, status, delay }: { n: Pt; status: Status; delay: number }) {
  const x = n.x + n.r * 0.72, y = n.y - n.r * 0.72;
  return (
    <g className="rm-a rm-pop" style={popIn(delay)}>
      <circle cx={x} cy={y} r={7.5} fill={STATUS[status]} opacity=".2" />
      <circle cx={x} cy={y} r={4.5} fill={STATUS[status]} stroke={FILL} strokeWidth="1.5" />
    </g>
  );
}

// a small rounded label pill, e.g. "Signed for x5" on a link
export function Pill({ x, y, text, delay, w, h = 22, color = BLUE, bg = BLUE_SOFT, ink = '#1d4ed8' }: { x: number; y: number; text: string; delay: number; w: number; h?: number; color?: string; bg?: string; ink?: string }) {
  return (
    <g className="rm-a rm-pop" style={popIn(delay)}>
      <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={h / 2} fill={bg} stroke={color} strokeOpacity=".45" strokeWidth="1" />
      <text x={x} y={y + 4} textAnchor="middle" className="rm-sub" fill={ink}>{text}</text>
    </g>
  );
}

// the dotted background used by the other light illustrations
export const GridBg = () => (
  <div className="ex-graphic-grid absolute inset-0 bg-[linear-gradient(to_right,#64748b12_1px,transparent_1px),linear-gradient(to_bottom,#64748b12_1px,transparent_1px)] bg-[size:24px_24px]" />
);

// a tree link for the phone layouts: straight down from A, then across into B's left side
export const elbow = (A: Pt, B: Pt) => `M${A.x} ${A.y + A.r + 3}V${B.y}H${B.x - B.r - 3}`;

// a record that starts faint and comes forward when research finds it
export const reveal = (delay: number) => ({ animation: `rm-reveal .5s cubic-bezier(0.65, 0, 0.35, 1) ${delay}s both` });
